import { useGetUsersByIds } from '../../../../../domain/Users';
import { RemoteConfigUtils } from '../../../../../utils/RemoteConfigUtils';
import { CustomSelect } from '../../../UsersList/components/CustomSelect';

/**
 * RecruitersCombobox component for selecting recruiters
 * Automatically fetches recruiters from remote config and displays them in a dropdown
 *
 * @param {Object} props - Component props
 * @param {Function} props.onChange - Callback function that receives an object { value: string, label: string }
 *   - value: Empty string for the "All" option, otherwise whatever `keyExtractor` returns for the
 *     selected recruiter — which defaults to `recruiter.id`. Both current consumers key by email,
 *     so in practice the value is the recruiter's email, not an id.
 *   - label: Display text — the "All" text, or the recruiter's email
 * @param {Object} [props.lang] - Language/text configuration
 * @param {string} [props.lang.allOptionsText='All'] - Text for the "All" option (defaults to "All")
 * @param {string} [props.value=''] - Currently selected value. Must match whatever `keyExtractor`
 *   returns (empty string for "All"); a value matching no option renders as "All".
 * @param {string} [props.label] - Label text displayed above the select
 * @param {Function} [props.keyExtractor] - Optional function to derive an option's value from a recruiter object. Falls back to recruiter.id if not provided or if it returns a falsy value.
 * @param {boolean} [props.isReadonly=false] - When true, renders a readonly input showing the selected option instead of a select
 *
 * @example
 * <RecruitersCombobox
 *   onChange={({ value, label }) => {
 *     console.log('Selected:', value, label);
 *     // value: '' for All, otherwise the recruiter's email (per the keyExtractor below)
 *     // label: 'All' or the recruiter's email
 *   }}
 *   lang={{ allOptionsText: 'All Recruiters' }}
 *   value=""
 *   label="Select Recruiter"
 *   keyExtractor={(recruiter) => recruiter.email}
 * />
 */
export const RecruitersCombobox = ({
  onChange,
  lang = {},
  value = '',
  label,
  keyExtractor,
  isReadonly = false,
}) => {
  const { data: recruiters } = useGetUsersByIds(RemoteConfigUtils.getPermanentRecruiterIds());

  const { allOptionsText = 'All' } = lang;

  // Prepare options with "All" option as first item
  const options = [
    { code: '', name: allOptionsText },
    ...(recruiters || []).map((recruiter) => ({
      code: keyExtractor?.(recruiter) || recruiter.id,
      name: recruiter.email,
    })),
  ];

  const handleChange = (_id, selectedValue) => {
    const selected = options.find((option) => option.code === selectedValue);

    if (selected) {
      onChange({ value: selected.code, label: selected.name });
    }
  };

  return (
    <CustomSelect
      id="recruiter"
      label={label}
      options={options}
      value={value}
      onChange={handleChange}
      isReadonly={isReadonly}
    />
  );
};
